<template>
  <header
    class="tw:sticky tw:top-0 tw:z-30 tw:flex tw:w-full tw:flex-col tw:gap-6 tw:bg-white tw:px-8"
    :class="[
      hasTopRow ? 'tw:pt-3' : 'tw:pt-8',
      $slots.tabs ? 'tw:pb-0' : 'tw:pb-6',
      props.noBottomMargin ? 'tw:mb-0' : 'tw:mb-5',
      !props.noBorder && 'tw:border-b tw:border-solid tw:border-border-secondary',
    ]">
    <!-- breadcrumb row -->
    <div
      v-if="hasTopRow"
      class="tw:-mx-8 tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-solid tw:border-border-secondary tw:px-8 tw:pb-3">
      <LeadBreadcrumbs :items="props.breadcrumbs" />
      <div class="tw:flex tw:items-center tw:gap-3">
        <slot name="top-right" />
        <BrandSwitcher v-if="brandSwitcherEnabled" class="tw:w-52" data-testid="header-brand-switcher" />
      </div>
    </div>

    <!-- title + actions row -->
    <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4">
      <div class="tw:flex tw:min-w-0 tw:items-center tw:gap-4">
        <div class="tw:min-w-0">
          <div class="tw:flex tw:items-center tw:gap-2">
            <h1
              class="tw:m-0! tw:text-gray-900!"
              :class="props.titleSize === 'sm' ? 'tw:text-18! tw:font-semibold!' : 'tw:text-30! tw:font-bold!'">
              {{ props.title }}
            </h1>
            <slot name="status" />
          </div>
          <p v-if="props.subtitle" class="tw:m-0! tw:mt-1 tw:text-14 tw:font-normal tw:text-gray-500!">
            {{ props.subtitle }}
          </p>
          <slot name="subtitle" />
        </div>
      </div>
      <div v-if="$slots['right-side']" class="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
        <slot name="right-side" />
      </div>
    </div>

    <slot name="bottom" />
    <slot />

    <!-- tabs row -->
    <div v-if="$slots.tabs" class="tw:-mx-8 tw:border-t tw:border-solid tw:border-border-secondary tw:px-8">
      <slot name="tabs" />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import BrandSwitcher from '@/components/generic/BrandSwitcher.vue';
import LeadBreadcrumbs from '@/components/lead-design/LeadBreadcrumbs.vue';
import { brandSwitcherEnabled } from '@/config/brands';

export interface Props {
  breadcrumbs?: { label: string; path: RouteLocationRaw }[];
  title: string;
  subtitle?: string;
  /** `lg` (default) uses the 30/bold DS title; `sm` uses 18/semibold for denser layouts. */
  titleSize?: 'sm' | 'lg';
  noBottomMargin?: boolean;
  noBorder?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
  titleSize: 'lg',
});

const slots = useSlots();
// the brand switcher (stage and local dev only) lives in this row, so it forces the row to show
const hasTopRow = computed(
  () => !!(props.breadcrumbs && props.breadcrumbs.length) || !!slots['top-right'] || brandSwitcherEnabled
);

useAutopilotContext('page_header', () => ({
  title: props.title,
  breadcrumbs: props.breadcrumbs,
}));
</script>
